import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { CheckCircle2, RefreshCw, Send, Trash2, XCircle, Save } from "lucide-react";
import { toast } from "sonner";
import {
  adminTelegramBroadcast,
  adminTelegramDeleteLog,
  adminTelegramGetConfig,
  adminTelegramLog,
  adminTelegramSaveConfig,
  adminTelegramStatus,
  type TelegramAutoConfig,
} from "@/lib/admin-telegram.functions";

export const Route = createFileRoute("/_authenticated/admin/telegram")({
  component: TelegramPage,
});

function TelegramPage() {
  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold">Telegram Control</h1>
        <p className="text-sm text-muted-foreground">
          Channel status, delivery log, manual broadcasts, and auto-briefing settings.
        </p>
      </div>
      <Tabs defaultValue="status" className="space-y-4">
        <TabsList>
          <TabsTrigger value="status">Status</TabsTrigger>
          <TabsTrigger value="broadcast">Broadcast</TabsTrigger>
          <TabsTrigger value="log">Delivery log</TabsTrigger>
          <TabsTrigger value="auto">Auto</TabsTrigger>
        </TabsList>
        <TabsContent value="status">
          <StatusTab />
        </TabsContent>
        <TabsContent value="broadcast">
          <BroadcastTab />
        </TabsContent>
        <TabsContent value="log">
          <LogTab />
        </TabsContent>
        <TabsContent value="auto">
          <AutoTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function StatusTab() {
  const qc = useQueryClient();
  const status = useServerFn(adminTelegramStatus);
  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["admin", "telegram-status"],
    queryFn: () => status(),
  });

  return (
    <Card className="border-border/60 bg-card/60 p-4">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold">Connection status</h2>
        <Button
          size="sm"
          variant="outline"
          onClick={() => qc.invalidateQueries({ queryKey: ["admin", "telegram-status"] })}
        >
          <RefreshCw className={`mr-1 h-3.5 w-3.5 ${isFetching ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>
      {isLoading && <p className="mt-3 text-sm text-muted-foreground">Pinging Telegram…</p>}
      {data && (
        <div className="mt-4 space-y-3">
          <div className="flex items-center gap-2 text-sm">
            {data.configured ? (
              <Badge variant="outline" className="border-emerald-500/50 text-emerald-500">
                <CheckCircle2 className="mr-1 h-3 w-3" /> Configured
              </Badge>
            ) : (
              <Badge variant="outline" className="border-red-500/50 text-red-500">
                <XCircle className="mr-1 h-3 w-3" /> Not configured
              </Badge>
            )}
            {data.error && <span className="text-xs text-red-500">{data.error}</span>}
          </div>
          {data.bot && (
            <div className="grid gap-3 md:grid-cols-2">
              <InfoBlock label="Bot">
                <div className="font-mono">@{data.bot.username}</div>
                <div className="text-xs text-muted-foreground">{data.bot.first_name}</div>
                <div className="text-xs text-muted-foreground">ID {data.bot.id}</div>
              </InfoBlock>
              {data.chat && (
                <InfoBlock label="Target chat">
                  <div className="font-medium">{data.chat.title ?? `Chat ${data.chat.id}`}</div>
                  <div className="text-xs text-muted-foreground">
                    Type {data.chat.type} · ID {String(data.chat.id)}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {data.chat.member_count !== null
                      ? `${data.chat.member_count} subscribers`
                      : "Member count unavailable"}
                  </div>
                </InfoBlock>
              )}
            </div>
          )}
          {!data.configured && (
            <p className="text-xs text-muted-foreground">
              Set <code>TELEGRAM_BOT_TOKEN</code> and <code>TELEGRAM_CHAT_ID</code> in the project
              secrets to enable this integration.
            </p>
          )}
        </div>
      )}
    </Card>
  );
}

function InfoBlock({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="rounded-md border border-border/60 bg-card/40 p-3">
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="mt-1 space-y-0.5 text-sm">{children}</div>
    </div>
  );
}

function BroadcastTab() {
  const qc = useQueryClient();
  const send = useServerFn(adminTelegramBroadcast);
  const [message, setMessage] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);

  const sendMut = useMutation({
    mutationFn: () => send({ data: { message, kind: "manual" } }),
    onSuccess: () => {
      toast.success("Broadcast sent");
      setMessage("");
      qc.invalidateQueries({ queryKey: ["admin", "telegram-log"] });
    },
    onError: (e: Error) => toast.error(e.message),
    onSettled: () => setConfirmOpen(false),
  });

  return (
    <Card className="border-border/60 bg-card/60 p-4">
      <div className="space-y-3">
        <Label className="text-xs">Message (Markdown, max 4000 chars)</Label>
        <Textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          rows={10}
          maxLength={4000}
          placeholder={"*FOREXMIND AI — Announcement*\n\nYour message here…"}
          className="font-mono text-sm"
        />
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>{message.length} / 4000</span>
          <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
            <AlertDialogTrigger asChild>
              <Button size="sm" disabled={!message.trim()}>
                <Send className="mr-1 h-3.5 w-3.5" /> Send broadcast
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Send to the Telegram channel?</AlertDialogTitle>
                <AlertDialogDescription>
                  This message will be delivered to every subscriber immediately. There is no undo.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={() => sendMut.mutate()} disabled={sendMut.isPending}>
                  {sendMut.isPending ? "Sending…" : "Send"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
        <p className="text-xs text-muted-foreground">
          Scheduling is not available — every broadcast is delivered immediately. Schedule support
          requires a cron worker (deferred).
        </p>
      </div>
    </Card>
  );
}

function LogTab() {
  const qc = useQueryClient();
  const list = useServerFn(adminTelegramLog);
  const del = useServerFn(adminTelegramDeleteLog);
  const { data, isLoading } = useQuery({
    queryKey: ["admin", "telegram-log"],
    queryFn: () => list(),
  });
  const delMut = useMutation({
    mutationFn: (id: string) => del({ data: { id } }),
    onSuccess: () => {
      toast.success("Log entry deleted");
      qc.invalidateQueries({ queryKey: ["admin", "telegram-log"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });
  const rows = data?.entries ?? [];

  return (
    <Card className="border-border/60 bg-card/60">
      <div className="border-b border-border/60 p-4 text-sm font-semibold">
        Delivery log ({rows.length})
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Time</TableHead>
            <TableHead>Kind</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Message</TableHead>
            <TableHead>Error</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.id}>
              <TableCell className="text-xs text-muted-foreground">
                {new Date(r.created_at).toLocaleString()}
              </TableCell>
              <TableCell>
                <Badge variant="outline" className="capitalize">
                  {r.kind}
                </Badge>
              </TableCell>
              <TableCell>
                {r.status === "sent" ? (
                  <Badge variant="outline" className="border-emerald-500/50 text-emerald-500">
                    sent
                  </Badge>
                ) : (
                  <Badge variant="outline" className="border-red-500/50 text-red-500">
                    failed
                  </Badge>
                )}
              </TableCell>
              <TableCell className="max-w-md truncate text-xs">{r.message}</TableCell>
              <TableCell className="max-w-xs truncate text-xs text-red-500">
                {r.error ?? "—"}
              </TableCell>
              <TableCell className="text-right">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => delMut.mutate(r.id)}
                  className="text-red-500 hover:text-red-600"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </TableCell>
            </TableRow>
          ))}
          {!isLoading && rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={6} className="p-6 text-center text-muted-foreground">
                No Telegram activity yet.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Card>
  );
}

function AutoTab() {
  const qc = useQueryClient();
  const get = useServerFn(adminTelegramGetConfig);
  const save = useServerFn(adminTelegramSaveConfig);
  const { data } = useQuery({
    queryKey: ["admin", "telegram-config"],
    queryFn: () => get(),
  });
  const [cfg, setCfg] = useState<TelegramAutoConfig>({
    daily_briefing: false,
    min_conviction: 80,
    signal_autosend: true,
  });
  useEffect(() => {
    if (data?.config) setCfg(data.config);
  }, [data]);
  const saveMut = useMutation({
    mutationFn: () => save({ data: cfg }),
    onSuccess: () => {
      toast.success("Settings saved");
      qc.invalidateQueries({ queryKey: ["admin", "telegram-config"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <Card className="border-border/60 bg-card/60 p-4">
      <div className="space-y-4">
        <Row
          title="Auto-send high-conviction signals"
          description="When a council signal exceeds the threshold below, push it to the channel automatically."
        >
          <Switch
            checked={cfg.signal_autosend}
            onCheckedChange={(c) => setCfg({ ...cfg, signal_autosend: c })}
          />
        </Row>
        <Row
          title="Minimum conviction score"
          description="Signals below this score will not auto-broadcast."
        >
          <Input
            type="number"
            min={0}
            max={100}
            value={cfg.min_conviction}
            onChange={(e) =>
              setCfg({ ...cfg, min_conviction: Math.max(0, Math.min(100, Number(e.target.value))) })
            }
            className="h-8 w-20"
          />
        </Row>
        <Row
          title="Daily market briefing"
          description="Posts a daily roundup to the channel. Requires a cron worker — flag only for now."
        >
          <Switch
            checked={cfg.daily_briefing}
            onCheckedChange={(c) => setCfg({ ...cfg, daily_briefing: c })}
          />
        </Row>
        {cfg.daily_briefing && (
          <p className="rounded border border-yellow-500/40 bg-yellow-500/10 p-2 text-xs text-yellow-500">
            Daily briefing scheduling isn't wired to a cron worker yet. This flag is stored, but no
            automated job will run until cron is set up.
          </p>
        )}
        <div className="flex justify-end">
          <Button size="sm" onClick={() => saveMut.mutate()}>
            <Save className="mr-1 h-3.5 w-3.5" /> Save settings
          </Button>
        </div>
      </div>
    </Card>
  );
}

function Row({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between gap-4 border-b border-border/60 pb-3 last:border-0 last:pb-0">
      <div>
        <div className="text-sm font-medium">{title}</div>
        <div className="text-xs text-muted-foreground">{description}</div>
      </div>
      <div>{children}</div>
    </div>
  );
}
